// Scripted report conversations for the FE-only demo.

export interface DemoReportMessage {
  id: number;
  role: 'user' | 'agent';
  content: string;
  sql_query: string | null;
  result_data: Array<Record<string, unknown>> | null;
  result_columns: string[] | null;
  created_at: string;
}

export interface DemoReportConversation {
  id: string;
  title: string;
  client_id: string | null;
  created_at: string;
  updated_at: string;
  messages: DemoReportMessage[];
}

export interface ScriptedAnswer {
  /** lowercase keywords — best word-overlap wins */
  keywords: string[];
  title: string; // conversation title if this starts a new one
  message: string;
  sql_query?: string;
  columns?: string[];
  data?: Array<Record<string, unknown>>;
  steps?: Array<{ tool: string; status: 'completed' | 'failed'; description?: string }>;
}

export const scriptedAnswers: ScriptedAnswer[] = [
  {
    keywords: ['разходи', 'доставчици', 'доставчик', 'юли'],
    title: 'Разходи по доставчици — юли 2026',
    message:
      'Ето разходите по доставчици за юли 2026 (по дата на фактурата). Общо **26 872,30 €** от 7 доставчика. Най-голямото перо е "Строй Инвест" — окончателната фактура по договор №3/2026.',
    sql_query:
      "SELECT k.Name AS Доставчик, COUNT(*) AS Фактури, SUM(d.Total) AS Сума\nFROM AccRec r\nJOIN AccRec_D d ON d.AccRec_FK = r.ID\nJOIN Kontragent k ON k.ID = r.Partner_FK\nWHERE r.DocDate >= '2026-07-01' AND r.ActionType = 103\nGROUP BY k.Name ORDER BY Сума DESC",
    columns: ['Доставчик', 'Фактури', 'Сума (EUR)'],
    data: [
      { 'Доставчик': 'Строй Инвест ООД', 'Фактури': 1, 'Сума (EUR)': '9 340,00' },
      { 'Доставчик': 'Мега Строй ЕООД', 'Фактури': 1, 'Сума (EUR)': '8 940,00' },
      { 'Доставчик': 'Спиди АД', 'Фактури': 1, 'Сума (EUR)': '4 380,00' },
      { 'Доставчик': 'Технополис ЕАД', 'Фактури': 1, 'Сума (EUR)': '3 378,00' },
      { 'Доставчик': 'Интерлийз АД', 'Фактури': 1, 'Сума (EUR)': '691,10' },
      { 'Доставчик': 'ОМВ България ООД', 'Фактури': 1, 'Сума (EUR)': '142,10' },
      { 'Доставчик': 'Виваком България ЕАД', 'Фактури': 1, 'Сума (EUR)': '101,42' },
    ],
    steps: [
      { tool: 'query_mirage', status: 'completed', description: 'Справка по доставчици' },
      { tool: 'get_turnover', status: 'completed', description: 'Кръстосана проверка на общата сума' },
    ],
  },
  {
    keywords: ['неплатени', 'падеж', 'задължения', 'плащане'],
    title: 'Неплатени фактури с наближаващ падеж',
    message:
      'Три фактури са с падеж през следващите 10 дни, общо **21 658,00 €**. Обърни внимание: плащането към "Мега Строй" е блокирано заради непотвърдена смяна на банкова сметка — падежът е 16.07.',
    sql_query:
      "SELECT k.Name, r.DocNo, r.DueDate, d.Total\nFROM AccRec r JOIN Kontragent k ON k.ID = r.Partner_FK\nWHERE r.PaidStatus = 0 AND r.DueDate <= '2026-07-18'\nORDER BY r.DueDate",
    columns: ['Доставчик', 'Фактура', 'Падеж', 'Сума (EUR)'],
    data: [
      { 'Доставчик': 'Технополис ЕАД', 'Фактура': '1000023841', 'Падеж': '12.07.2026', 'Сума (EUR)': '3 378,00' },
      { 'Доставчик': 'Мега Строй ЕООД', 'Фактура': '0000000456', 'Падеж': '16.07.2026', 'Сума (EUR)': '8 940,00 ⚠️' },
      { 'Доставчик': 'Строй Инвест ООД', 'Фактура': '0000000912', 'Падеж': '18.07.2026', 'Сума (EUR)': '9 340,00' },
    ],
    steps: [
      { tool: 'query_mirage', status: 'completed', description: 'Отворени задължения по падеж' },
    ],
  },
  {
    keywords: ['оборот', 'месеци', 'приходи', 'тренд', 'полугодие'],
    title: 'Оборот по месеци — 2026',
    message:
      'Оборотът на "Ромил ООД" за първото полугодие на 2026: общо **1 118 400 €** приходи (+11% спрямо същия период на 2025). Най-силен месец е юни; спадът през февруари е сезонен (същият модел като 2025).',
    sql_query:
      "SELECT FORMAT(r.DocDate, 'yyyy-MM') AS Месец, SUM(d.Total) AS Приходи\nFROM AccRec r JOIN AccRec_D d ON d.AccRec_FK = r.ID\nWHERE r.ActionType = 102 AND r.DocDate >= '2026-01-01'\nGROUP BY FORMAT(r.DocDate, 'yyyy-MM') ORDER BY Месец",
    columns: ['Месец', 'Приходи (EUR)', 'Δ спрямо 2025'],
    data: [
      { 'Месец': 'Януари', 'Приходи (EUR)': '168 200', 'Δ спрямо 2025': '+9%' },
      { 'Месец': 'Февруари', 'Приходи (EUR)': '142 800', 'Δ спрямо 2025': '+6%' },
      { 'Месец': 'Март', 'Приходи (EUR)': '189 500', 'Δ спрямо 2025': '+12%' },
      { 'Месец': 'Април', 'Приходи (EUR)': '196 300', 'Δ спрямо 2025': '+10%' },
      { 'Месец': 'Май', 'Приходи (EUR)': '204 100', 'Δ спрямо 2025': '+13%' },
      { 'Месец': 'Юни', 'Приходи (EUR)': '217 500', 'Δ спрямо 2025': '+15%' },
    ],
    steps: [
      { tool: 'get_turnover', status: 'completed', description: 'Месечен оборот 2026' },
      { tool: 'get_turnover', status: 'completed', description: 'Сравнение с 2025' },
    ],
  },
];

export const fallbackAnswer: Omit<ScriptedAnswer, 'keywords'> = {
  title: 'Нова справка',
  message:
    'В демо средата са налични три примерни справки — опитайте с някой от тези въпроси:\n\n' +
    '- „Какви са разходите по доставчици за юли?“\n' +
    '- „Кои фактури са неплатени с наближаващ падеж?“\n' +
    '- „Покажи оборота по месеци за 2026“',
};

// Seeded conversation visible in the sidebar from the start.
export const seededReportConversations: DemoReportConversation[] = [
  {
    id: 'report-conv-1',
    title: 'Разходи по доставчици — юли 2026',
    client_id: 'client-romil',
    created_at: '2026-07-03T09:40:00Z',
    updated_at: '2026-07-03T09:42:00Z',
    messages: [
      {
        id: 1,
        role: 'user',
        content: 'Какви са разходите по доставчици за юли?',
        sql_query: null,
        result_data: null,
        result_columns: null,
        created_at: '2026-07-03T09:40:00Z',
      },
      {
        id: 2,
        role: 'agent',
        content: scriptedAnswers[0].message,
        sql_query: scriptedAnswers[0].sql_query ?? null,
        result_data: scriptedAnswers[0].data ?? null,
        result_columns: scriptedAnswers[0].columns ?? null,
        created_at: '2026-07-03T09:42:00Z',
      },
    ],
  },
];
